'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * A muted, looping feature video that only plays while it is on screen.
 *
 * - Plays/pauses via IntersectionObserver so we never burn battery or
 *   bandwidth on footage nobody is looking at.
 * - `preload="none"` + poster keeps it out of the critical path (LCP).
 * - Under `prefers-reduced-motion` it never autoplays; instead the native
 *   controls are shown so the visitor can play it deliberately.
 */
export function AutoPlayVideo({
  src,
  poster,
  label,
  className,
}: {
  src: string;
  poster: string;
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          el.play().catch(() => {
            // Autoplay can be blocked by the browser; the poster remains.
          });
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      controls={reducedMotion}
      aria-label={label}
      className={className}
    />
  );
}
